import { useEffect, useRef, useState } from 'react';
import { localeTag } from './locale';

// Speech: the browser's own recognition (Chrome sends the audio to Google, Safari to Apple).
// Used by "Einfach sagen" and, since 1.22, "Was ist da?" (Mike, 17.10.: "einsprechen anstatt
// schreiben").

interface Recognition {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start(): void;
  stop(): void;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onstart?: (() => void) | null;
  onaudiostart?: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((e?: { error?: string }) => void) | null;
}
type RecognitionCtor = new () => Recognition;

const SpeechRecognition = (): RecognitionCtor | undefined =>
  (
    window as unknown as {
      SpeechRecognition?: RecognitionCtor;
      webkitSpeechRecognition?: RecognitionCtor;
    }
  ).SpeechRecognition ??
  (window as unknown as { webkitSpeechRecognition?: RecognitionCtor }).webkitSpeechRecognition;

/**
 * The spoken text from the recognition's results. Desktop Chrome sends one result per phrase;
 * Chrome on Android sends the growing sentence as ever longer results ("erstelle", "erstelle
 * mir", …) – joining those repeated every word (Mike, 02.10.). So a result that continues the
 * last one replaces it, one that is already contained in it is skipped.
 */
export function joinResults(list: string[]): string {
  const parts: string[] = [];
  for (const raw of list) {
    const t = raw.trim();
    if (!t) continue;
    const last = parts[parts.length - 1];
    if (last !== undefined && t.toLowerCase().startsWith(last.toLowerCase()))
      parts[parts.length - 1] = t;
    else if (last !== undefined && last.toLowerCase().startsWith(t.toLowerCase())) continue;
    else parts.push(t);
  }
  return parts.join(' ');
}

/** No recognition of its own should run longer than this, even if the user forgets to stop. */
const MAX_MS = 3 * 60_000;
/** A browser that ends at once again and again (no microphone, no network) must not loop. */
const MAX_RESTARTS = 20;

export type DictationState = 'idle' | 'starting' | 'listening';

/**
 * Speak into a text field (1.27, Mike 26.10.: "hört zu früh auf", "ich sehe nichts", "die ersten
 * Wörter fehlen oft"): listens until the user stops – a pause does not end it, the browser's own
 * end restarts quietly –, shows the words while speaking, and says "Jetzt sprechen" only once the
 * microphone really runs (before, the first words got lost). What was typed before stays.
 * `onStopped` gets the final text when the user stopped (e.g. to fetch suggestions right away).
 */
export function useDictation(
  text: string,
  setText: (t: string) => void,
  onStopped?: (text: string) => void,
) {
  const [state, setState] = useState<DictationState>('idle');
  const [blocked, setBlocked] = useState(false);
  const recognition = useRef<Recognition>(undefined);
  const session = useRef({
    stopped: true,
    blocked: false,
    before: '',
    spoken: '',
    startedAt: 0,
    heard: false,
    restarts: 0,
  });
  const canSpeak = typeof window !== 'undefined' && Boolean(SpeechRecognition());
  useEffect(
    () => () => {
      session.current.stopped = true;
      recognition.current?.stop();
    },
    [],
  );

  const full = () => {
    const { before, spoken } = session.current;
    return [before, spoken].filter(Boolean).join(' ');
  };

  const run = () => {
    const Ctor = SpeechRecognition();
    if (!Ctor) return;
    const r = new Ctor();
    r.lang = localeTag;
    r.interimResults = true;
    r.continuous = true;
    const ready = () => setState('listening');
    r.onstart = ready;
    r.onaudiostart = ready;
    r.onresult = (e) => {
      const said = joinResults(Array.from(e.results).map((x) => x[0]?.transcript ?? ''));
      session.current.spoken = said;
      if (said) session.current.heard = true;
      setText(full());
    };
    r.onerror = (e) => {
      // a blocked microphone never works – say so instead of a button that does nothing
      if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') {
        setBlocked(true);
        session.current.stopped = true;
        session.current.blocked = true;
      }
    };
    r.onend = () => {
      const s = session.current;
      // the browser ends on its own (silence, Android after a few seconds): go on listening,
      // with what was said so far kept in front
      // only after a round that heard something – one that ends with nothing new means the
      // browser stopped for good (no speech, no network), restarting would only loop
      if (!s.stopped && s.heard && s.restarts < MAX_RESTARTS && Date.now() - s.startedAt < MAX_MS) {
        s.before = full();
        s.spoken = '';
        s.heard = false;
        s.restarts++;
        setState('starting');
        run();
        return;
      }
      setState('idle');
      if (s.stopped && !s.blocked) onStopped?.(full());
    };
    recognition.current = r;
    r.start();
  };

  const listen = () => {
    if (state !== 'idle') {
      session.current.stopped = true;
      recognition.current?.stop();
      return;
    }
    setBlocked(false);
    session.current = {
      stopped: false,
      blocked: false,
      before: text.trim(),
      spoken: '',
      startedAt: Date.now(),
      heard: false,
      restarts: 0,
    };
    setState('starting');
    run();
  };

  return { canSpeak, state, listening: state !== 'idle', blocked, listen };
}
